import React, { useState, useEffect, useMemo } from 'react';
import { 
  Database, 
  Search, 
  Filter, 
  ShieldCheck, 
  ExternalLink, 
  CheckCircle2, 
  Clock, 
  Building, 
  MapPin, 
  ChevronRight, 
  FileText,
  AlertCircle,
  Sparkles,
  Lock,
  ArrowRight
} from 'lucide-react';
import { centralRfxService, CentralRfxItem, StateTableMeta, UserRfxItem } from '../services/centralRfxService';
import { StateVendorParticipationModal } from './StateVendorParticipationModal';
import { useAuth } from '../AuthContext';

interface CentralStateSolicitationsSectionProps {
  onOpenInUserWorkspace: (solicitationId: string) => void;
  userParticipatedIds: Set<string>;
  onItemCopied: (item: UserRfxItem) => void;
}

export const CentralStateSolicitationsSection: React.FC<CentralStateSolicitationsSectionProps> = ({
  onOpenInUserWorkspace,
  userParticipatedIds,
  onItemCopied
}) => {
  const { user } = useAuth();

  const [stateTables, setStateTables] = useState<StateTableMeta[]>([]);
  const [selectedStateCode, setSelectedStateCode] = useState<string>('ALL');
  const [items, setItems] = useState<CentralRfxItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('open');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  // Modal State
  const [selectedForParticipation, setSelectedForParticipation] = useState<CentralRfxItem | null>(null);
  const [isParticipationModalOpen, setIsParticipationModalOpen] = useState(false);

  // 1. Load state tables metadata
  useEffect(() => {
    async function loadMeta() {
      try {
        const meta = await centralRfxService.fetchStateTablesMeta();
        if (meta && meta.length > 0) {
          setStateTables(meta);
        } else {
          // Fallback state tables list
          setStateTables([
            { stateCode: 'TX', stateName: 'Texas', tableName: 'central_rfx_tx', portalUrl: 'https://www.txsmartbuy.gov/esbd', totalCount: 3, openCount: 3, lastScannedAt: new Date().toISOString() },
            { stateCode: 'CA', stateName: 'California', tableName: 'central_rfx_ca', portalUrl: 'https://caleprocure.ca.gov', totalCount: 2, openCount: 2, lastScannedAt: new Date().toISOString() },
            { stateCode: 'FL', stateName: 'Florida', tableName: 'central_rfx_fl', portalUrl: 'https://vendor.myfloridamarketplace.com', totalCount: 1, openCount: 1, lastScannedAt: new Date().toISOString() },
            { stateCode: 'NY', stateName: 'New York', tableName: 'central_rfx_ny', portalUrl: 'https://www.nyscr.ny.gov', totalCount: 1, openCount: 1, lastScannedAt: new Date().toISOString() },
            { stateCode: 'NC', stateName: 'North Carolina', tableName: 'central_rfx_nc', portalUrl: 'https://evp.nc.gov', totalCount: 10, openCount: 10, lastScannedAt: new Date().toISOString() },
            { stateCode: 'AZ', stateName: 'Arizona', tableName: 'central_rfx_az', portalUrl: 'https://app.az.gov', totalCount: 6, openCount: 6, lastScannedAt: new Date().toISOString() },
            { stateCode: 'CO', stateName: 'Colorado', tableName: 'central_rfx_co', portalUrl: 'https://codpa-vss.cloud.cgifederal.com', totalCount: 4, openCount: 4, lastScannedAt: new Date().toISOString() }
          ]);
        }
      } catch (err) {
        console.warn('Failed to load state tables meta:', err);
      }
    }
    loadMeta();
  }, []);

  // 2. Load Central RFX items
  useEffect(() => {
    let isMounted = true;
    async function loadItems() {
      setLoading(true);
      try {
        const res = await centralRfxService.fetchCentralRfxItems({
          stateCode: selectedStateCode !== 'ALL' ? selectedStateCode : undefined,
          status: selectedStatus !== 'ALL' ? selectedStatus : undefined,
          category: selectedCategory !== 'All' ? selectedCategory : undefined,
          search: searchQuery.trim() || undefined
        });
        if (isMounted) {
          setItems(res || []);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load central RFX items:', err);
        if (isMounted) setLoading(false);
      }
    }
    loadItems();
    return () => { isMounted = false; };
  }, [selectedStateCode, selectedStatus, selectedCategory, searchQuery]);

  // Derived categories
  const categories = useMemo(() => {
    const set = new Set<string>();
    items.forEach(i => { if (i.category) set.add(i.category); });
    return ['All', ...Array.from(set)].slice(0, 10);
  }, [items]);

  const handleParticipateClick = (item: CentralRfxItem) => {
    setSelectedForParticipation(item);
    setIsParticipationModalOpen(true);
  };

  const handleParticipationSuccess = (copiedItem: UserRfxItem) => {
    onItemCopied(copiedItem);
    onOpenInUserWorkspace(copiedItem.solicitationId);
  };

  return (
    <div className="space-y-6" id="central-rfx-section">
      {/* Official Architecture & Regulatory Banner */}
      <div className="p-4.5 bg-slate-900 text-white rounded-2xl shadow-sm border border-slate-800 relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="space-y-1 relative z-10 max-w-3xl">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded">
              Centralized Server-Side RFX Data Architecture
            </span>
            <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded">
              Internal Hourly Delta Scanning Active
            </span>
          </div>
          <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
            <Database className="w-4.5 h-4.5 text-indigo-400" />
            Central Public Procurement Registry (Read-Only Portal Directory)
          </h2>
          <p className="text-xs text-slate-300 leading-relaxed">
            Per-state RFX tables are continuously ingested server-side from official state procurement portals. End users have <strong>Read-Only</strong> access to browse public opportunities. Access to full solicitation specifications, line-item pricing workbooks, and proposal generation requires confirming participation intent and verifying registered vendor standing in that state.
          </p>
        </div>

        <div className="shrink-0 flex items-center gap-3 relative z-10 text-xs text-slate-400 border-t md:border-t-0 md:border-l border-slate-800 pt-3 md:pt-0 md:pl-4">
          <div className="text-center">
            <span className="block text-lg font-extrabold text-white">{stateTables.length || 7}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400">State Tables</span>
          </div>
          <div className="text-center">
            <span className="block text-lg font-extrabold text-emerald-400">
              {stateTables.reduce((acc, curr) => acc + (curr.openCount || 0), 0) || items.length}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Open Bids</span>
          </div>
        </div>
      </div>

      {/* Per-State Selector Bar */}
      <div className="bg-white p-3 border border-gray-200 rounded-xl shadow-2xs space-y-2">
        <div className="flex items-center justify-between px-1">
          <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-indigo-600" />
            Select State RFXItems Table:
          </span>
          <span className="text-[11px] text-gray-500">
            Current: <strong className="text-gray-900">{selectedStateCode === 'ALL' ? 'All Registered States' : `central_rfx_${selectedStateCode.toLowerCase()}`}</strong>
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
          <button
            onClick={() => setSelectedStateCode('ALL')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
              selectedStateCode === 'ALL'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            All States ({stateTables.reduce((acc, curr) => acc + curr.totalCount, 0)})
          </button>

          {stateTables.map(st => (
            <button
              key={st.stateCode}
              onClick={() => setSelectedStateCode(st.stateCode)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                selectedStateCode === st.stateCode
                  ? 'bg-indigo-600 text-white shadow-2xs'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>{st.stateName}</span>
              <span className={`px-1.5 py-0.2 text-[10px] font-mono rounded ${
                selectedStateCode === st.stateCode ? 'bg-indigo-700 text-white' : 'bg-white text-gray-600 border border-gray-200'
              }`}>
                {st.openCount}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search solicitations, IDs, agencies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs bg-white border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-indigo-500 shadow-2xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 text-xs font-medium bg-white border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-indigo-500 shadow-2xs"
          >
            <option value="ALL">All Statuses</option>
            <option value="open">Open / Active</option>
            <option value="closed">Closed / Past</option>
          </select>

          {/* Category Filter */}
          {categories.length > 1 && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 text-xs font-medium bg-white border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-indigo-500 shadow-2xs max-w-[200px]"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* State RFXItems Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-200 text-gray-500 font-semibold text-[11px] uppercase tracking-wider">
                <th className="py-3 px-4">Solicitation ID</th>
                <th className="py-3 px-4">Opportunity Title & Agency</th>
                <th className="py-3 px-3">State</th>
                <th className="py-3 px-3">Category</th>
                <th className="py-3 px-3">Due Date</th>
                <th className="py-3 px-3">Est. Budget</th>
                <th className="py-3 px-4 text-right">Access & Participation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mb-2" />
                    <p className="text-xs">Querying Central State RFX Data Tables...</p>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500">
                    <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="font-semibold text-gray-800">No solicitations match your active filters.</p>
                    <p className="text-xs text-gray-400 mt-1">Try resetting search keywords or selecting another state table.</p>
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const isParticipating = userParticipatedIds.has(item.solicitationId);
                  return (
                    <tr key={item.solicitationId} className="hover:bg-slate-50/70 transition-colors group">
                      {/* ID */}
                      <td className="py-3.5 px-4 font-mono font-bold text-indigo-700 whitespace-nowrap">
                        <span className="bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">
                          {item.solicitationId}
                        </span>
                      </td>

                      {/* Title & Agency */}
                      <td className="py-3.5 px-4 max-w-md">
                        <div className="font-semibold text-gray-950 group-hover:text-indigo-600 transition-colors line-clamp-1">
                          {item.title}
                        </div>
                        <div className="text-[11px] text-gray-500 flex items-center gap-1.5 mt-0.5">
                          <Building className="w-3 h-3 text-gray-400" />
                          <span className="truncate">{item.agency}</span>
                        </div>
                      </td>

                      {/* State */}
                      <td className="py-3.5 px-3 whitespace-nowrap">
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-700 border border-slate-200">
                          {item.stateCode || item.state}
                        </span>
                      </td>

                      {/* Category */}
                      <td className="py-3.5 px-3 max-w-[150px]">
                        <span className="truncate block text-gray-600 text-[11px]" title={item.category}>
                          {item.category}
                        </span>
                      </td>

                      {/* Due Date */}
                      <td className="py-3.5 px-3 whitespace-nowrap text-gray-800 font-medium">
                        <div className="flex items-center gap-1 text-[11px]">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span>{item.dueDate || 'Open'}</span>
                        </div>
                      </td>

                      {/* Budget */}
                      <td className="py-3.5 px-3 whitespace-nowrap text-emerald-700 font-semibold">
                        ${(item.pricing || 75000).toLocaleString()}
                      </td>

                      {/* Participation & Details Gate Action */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        {isParticipating ? (
                          <div className="inline-flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              In My Database
                            </span>
                            <button
                              onClick={() => onOpenInUserWorkspace(item.solicitationId)}
                              className="px-2.5 py-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer"
                            >
                              Open Details
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleParticipateClick(item)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-2xs transition-all cursor-pointer group/btn"
                          >
                            <ShieldCheck className="w-3.5 h-3.5 text-indigo-200" />
                            <span>Participate & Access Details</span>
                            <ChevronRight className="w-3 h-3 opacity-70 group-hover/btn:translate-x-0.5 transition-transform" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* State Vendor Participation Modal */}
      <StateVendorParticipationModal
        isOpen={isParticipationModalOpen}
        onClose={() => {
          setIsParticipationModalOpen(false);
          setSelectedForParticipation(null);
        }}
        solicitation={selectedForParticipation}
        onSuccess={handleParticipationSuccess}
      />
    </div>
  );
};
